import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AccountComponent } from './account/account.component';
import { FinalUserDashboardComponent } from './final-user-dashboard/final-user-dashboard.component';
import { FinalUsersComponent } from './final-users/final-users.component';
import { HomepageComponent } from './homepage/homepage.component';
import { NewTicketComponent } from './new-ticket/new-ticket.component';
import { RegisterComponent } from './register/register.component';
import { RegistrationCompleteComponent } from './registration-complete/registration-complete.component';
import { SentPasswordComponent } from './sent-password/sent-password.component';
import { TicketComponent } from './ticket/ticket.component';
import { BannerComponent } from './banner/banner.component';
import { BannerFormComponent } from './banner-form/banner-form.component';
import { LegalDocumentsComponent } from './legal-documents/legal-documents.component';
import { LegalDocumentsPreviewComponent } from './legal-documents-preview/legal-documents-preview.component';
import { LegalDocumentFormComponent } from './legal-document-form/legal-document-form.component';

import { AngularFireAuthGuard, redirectUnauthorizedTo } from '@angular/fire/compat/auth-guard';
import { TermsAndConditionsComponent } from '@shared/components/terms-and-conditions/terms-and-conditions.component';
import { firstLoginGuard } from '@shared/guards/first-login.guard';
import { roleGuard } from '@shared/guards/role.guard';
import { termsAndConditionsGuard } from '@shared/guards/terms-and-conditions.guard';
import { Roles } from '@shared/models/types';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { NewPasswordComponent } from './new-password/new-password.component';
import { NewRequestComponent } from './new-request/new-request.component';
import { RequestComponent } from './request/request.component';
import { AnalyticsPageComponent } from './analytics-page/analytics-page.component';
import { UserComponent } from './user/user.component';
import { UserFormComponent } from './user-form/user-form.component';
import { RepositoryComponent } from './repository/repository.component';
import { RequiredDocumentComponent } from './required-document/required-document.component';
import { RequiredDocumentFormComponent } from './required-document-form/required-document-form.component';
import { SalesOrderFormComponent } from './sales-order-form/sales-order-form.component';
import { SalesOrderDocumentsComponent } from './sales-order-documents/sales-order-documents.component';
import { OdvDocumentsFormComponent } from './odv-documents-form/odv-documents-form.component';
import { LearningComponent } from './learning/learning.component';
import { LearningFormComponent } from './learning-form/learning-form.component';
import { LearningDetailComponent } from './learning-detail/learning-detail.component';
import { LearningFeedbackFormComponent } from './learning-feedback-form/learning-feedback-form.component';
import { CompanyGroupComponent } from './company-group/company-group.component';
import { CompanyGroupFormComponent } from './company-group-form/company-group-form.component';
import { CompanyGroupUsersComponent } from './company-group-users/company-group-users.component';
import { MachineModulesComponent } from './machine-modules/machine-modules.component';
import { MachineModulesFormComponent } from './machine-modules-form/machine-modules-form.component';

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);

const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent),
    data: { enableSharedComponent: false, animation: 'LoginPage' }
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
    data: { enableSharedComponent: false, animation: 'ForgotPasswordPage' }
  },
  {
    path: 'new-password',
    component: NewPasswordComponent,
    data: { enableSharedComponent: false, animation: 'NewPasswordPage' }
  },
  {
    path: 'register',
    component: RegisterComponent,
    data: { enableSharedComponent: false, animation: 'RegisterPage' }
  },
  {
    path: 'sent-password',
    component: SentPasswordComponent,
    data: { enableSharedComponent: false, animation: 'SentPasswordPage' }
  },
  {
    path: 'registration-complete',
    component: RegistrationCompleteComponent,
    data: { enableSharedComponent: false, animation: 'RegistrationCompletePage' }
  },
  {
    path: 'home',
    component: HomepageComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN],
      animation: 'HomePage'
    }
  },
  {
    path: 'ticket',
    component: TicketComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER],
      animation: 'TicketPage'
    }
  },
  {
    path: 'account',
    component: AccountComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER],
      animation: 'AccountPage'
    }
  },
  {
    path: 'ticket/new',
    component: NewTicketComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN]
    }
  },
  {
    path: 'request',
    component: RequestComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER, Roles.ADMIN]
    }
  },
  {
    path: 'request/new',
    component: NewRequestComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER, Roles.ADMIN]
    }
  },
  {
    path: 'request/new/:id/:number',
    component: NewRequestComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER, Roles.ADMIN]
    }
  },
  {
    path: 'final-users',
    component: FinalUsersComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.PARTNER]
    }
  },
  {
    path: 'final-user-dashboard',
    component: FinalUserDashboardComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.PARTNER]
    }
  },
  {
    path: 'terms-and-conditions',
    component: TermsAndConditionsComponent,
    canActivate: [AngularFireAuthGuard, firstLoginGuard, roleGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: false,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER, Roles.ADMIN]
    }
  },
  {
    path: 'change-password',
    component: ChangePasswordComponent,
    canActivate: [AngularFireAuthGuard, roleGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: false,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER, Roles.ADMIN]
    }
  },
  {
    path: 'user',
    component: UserComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'user/new',
    component: UserFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'user/:id',
    component: UserFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'banner',
    component: BannerComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'machine-modules',
    component: MachineModulesComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'machine-modules/:serialNumber',
    component: MachineModulesFormComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'banner/new',
    component: BannerFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'banner/:id',
    component: BannerFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'legal-documents',
    component: LegalDocumentsComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'legal-documents/preview',
    component: LegalDocumentsPreviewComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'legal-documents/:id',
    component: LegalDocumentFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'repository',
    component: RepositoryComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'repository/required-documents',
    component: RequiredDocumentComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'repository/required-documents/new',
    component: RequiredDocumentFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'repository/required-documents/:id',
    component: RequiredDocumentFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'repository/sales-order/new',
    component: SalesOrderFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'repository/sales-order/:id',
    component: SalesOrderFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN, Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER]
    }
  },
  {
    path: 'repository/sales-order/:id/documents',
    component: SalesOrderDocumentsComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'repository/sales-order-documents/:id/form',
    component: OdvDocumentsFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'learning',
    component: LearningComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'learning/new',
    component: LearningFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'learning/:id',
    component: LearningDetailComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'learning/:id/edit',
    component: LearningFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'learning/:id/feedback',
    component: LearningFeedbackFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.ADMIN, Roles.PARTNER]
    }
  },
  {
    path: 'company-group-users',
    component: CompanyGroupUsersComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER, Roles.ADMIN]
    }
  },
  {
    path: 'company-group',
    component: CompanyGroupComponent,
    pathMatch: 'full',
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'company-group/new',
    component: CompanyGroupFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'company-group/:id',
    component: CompanyGroupFormComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN]
    }
  },
  {
    path: 'analytics',
    component: AnalyticsPageComponent,
    canActivate: [AngularFireAuthGuard, roleGuard, firstLoginGuard, termsAndConditionsGuard],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
      enableSharedComponent: true,
      roles: [Roles.ADMIN, Roles.CUSTOMER, Roles.CUSTOMER_PARTNER, Roles.PARTNER]
    }
  },
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full',
    data: { enableSharedComponent: false }
  },
  // Wildcard route
  { path: '**', redirectTo: '/home' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
